// layouts/IntegrationsLayout.tsx
import { ReactNode, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Ticket, Mail, Phone } from "lucide-react";
import { Whatsapp } from "../components/ui/Icon";

interface IntegrationsLayoutProps {
  children: ReactNode;
  role?: string;
}

export default function IntegrationsLayout({ children, role = "entreprise" }: IntegrationsLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const basePath = `/${role}/integrations`;

  const tabItems = [
    {
      label: "Tickets",
      path: "/tickets",
      icon: Ticket,
      description: "Widget de support",
    },
    {
      label: "SMS",
      path: "/sms",
      icon: Ticket,
      description: "Widget de support",
    },

    {
      label: "Whatsapp ",
      path: "/whatsapp",
      icon: Whatsapp,
      description: "Widget de support",
    },
    {
      label: "Contact Mail",
      path: "/email",
      icon: Mail,
      description: "Formulaire de contact",
    },
    {
      label: "Appels WebRTC",
      path: "/webrtc",
      icon: Phone,
      description: "VoIP depuis le navigateur",
    },
  ];

  const isActive = (path: string) => {
    const currentPath = location.pathname;
    if (path === "") {
      return currentPath === basePath;
    }
    return currentPath === `${basePath}${path}` || currentPath.startsWith(`${basePath}${path}/`);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const activeIndex = tabItems.findIndex((item) => isActive(item.path));
    if (activeIndex !== -1 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeTab = container.children[activeIndex] as HTMLElement;

      if (activeTab) {
        const containerWidth = container.offsetWidth;
        const tabLeft = activeTab.offsetLeft;
        const tabWidth = activeTab.offsetWidth;

        const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;
        container.scrollTo({ left: scrollPosition, behavior: "smooth" });
      }
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="relative flex items-center px-4">
            <button
              onClick={scrollLeft}
              className="absolute left-0 z-10 bg-white h-full px-2 shadow-right hover:text-primary transition-colors hidden md:flex items-center"
              style={{ boxShadow: "4px 0 8px -4px rgba(0,0,0,0.05)" }}
            >
              <ChevronLeft size={18} className="text-gray-400 hover:text-gray-600" />
            </button>

            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-x-auto scrollbar-hide py-2"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              <div className="flex space-x-1 min-w-max">
                {tabItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(`${basePath}${item.path}`)}
                      className={`
                                                flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap
                                                ${
                                                  active
                                                    ? "bg-blue-50 text-blue-700 shadow-sm"
                                                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                                                }
                                            `}
                    >
                      <Icon className={`w-4 h-4 ${active ? "text-blue-600" : "text-gray-400"}`} />
                      <span>{item.label}</span>
                      <span className={`text-[10px] hidden lg:inline ${active ? "text-blue-500" : "text-gray-400"}`}>
                        {item.description}
                      </span>
                      {active && <span className="w-1.5 h-1.5 bg-blue-600 rounded-full ml-0.5"></span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={scrollRight}
              className="absolute right-0 z-10 bg-white h-full px-2 shadow-left hover:text-primary transition-colors hidden md:flex items-center"
              style={{ boxShadow: "-4px 0 8px -4px rgba(0,0,0,0.05)" }}
            >
              <ChevronRight size={18} className="text-gray-400 hover:text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">{children}</div>

      <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
    </div>
  );
}
